import React from "react";
import * as BS from "react-bootstrap";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import * as redux from "redux";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import { ErrorCodes } from "../../../../domain";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import Settings from "../../../../settings";
import { AppState } from "../../../../store";
import { AuthHelper, Utility } from "../../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    api: new Api(state),
    clientId: state.auth.clientId,
    clientSecret: state.auth.clientSecret,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
});

interface OwnProps {
    onSuccess: (accessToken: string, refreshToken: string) => void;
    onUserChange: () => void;
    returnUrl: string;
    user?: M.User.BasicProfile;
}

interface ConnectedState {
    api: Api;
    clientId: string;
    clientSecret: string;
}

interface ConnectedDispatch {
}

interface OwnState {
    emailErrors: string[];
    emailSent: boolean;
    isForgotPw: boolean;
    sendingEmail: boolean;
}

class PasswordFormComponent extends ComponentWithValidatedForm<ConnectedState & ConnectedDispatch & OwnProps, OwnState> {

    public constructor(props: any) {
        super(props);

        const formModel = new Forms.Account.SignInPassword();
        this.state = {
            ...this.getInitialState(formModel),
            emailErrors: [],
            emailSent: false,
            isForgotPw: false,
            sendingEmail: false,
        };
    }

    public componentDidMount() {
        this.focusPassword();
    }

    public componentWillReceiveProps(nextProps: OwnProps) {
        if (!this.props.user && nextProps.user) {
            setTimeout(() => {
                this.focusPassword();
            }, 225);
        }
    }

    public render() {
        const pwFormButtons = this.state.formProcessing
            ? <div>
                <span className="btn btn-primary" style={{ float: "right" }}>&nbsp;&nbsp;<Comp.Generic.Icon icon="spinner fa-spin" />&nbsp;&nbsp;</span>
            </div>
            : <div>
                <BS.Button bsStyle="link" onClick={() => this.toggleForgotPw()}>Forgot password?</BS.Button>
                <input className="btn btn-primary" style={{ float: "right" }} type="submit" value="Next" />
            </div>;

        const user = this.props.user;
        const isForgotPw = this.state.isForgotPw;

        const emailErrors = this.state.emailErrors.length
            ? <div>
                <BS.Alert bsStyle="danger">
                    <p>Failed to send email. Please try again later.</p>
                </BS.Alert>
                <p>
                    <a href="#" onClick={(e) => { this.toggleForgotPw(); e.preventDefault(); }}>Return</a>
                    {" or "}
                    <a href="#" onClick={(e) => { this.toggleSendingEmail(false, [], false); e.preventDefault(); }}>trying again</a>.
                </p>
            </div>
            : null;

        let forgotPwView = null;
        if (!this.state.emailSent && !this.state.emailErrors.length) {
            const forgotPwButtons = this.state.sendingEmail
                ? <div>
                    <span className="btn btn-primary" style={{ float: "right" }}>&nbsp;&nbsp;<Comp.Generic.Icon icon="spinner fa-spin" />&nbsp;&nbsp;</span>
                </div>
                : <div>
                    <BS.Button bsStyle="link" onClick={() => this.toggleForgotPw()}>&laquo; Return</BS.Button>
                    <BS.Button bsStyle="primary" onClick={() => this.onSendEmail()} style={{ float: "right" }}>Send email</BS.Button>
                </div>;

            forgotPwView = <div>
                <p>To recover your password, Press "Send email" to receive an email with instructions on how to reset your password.</p>
                {forgotPwButtons}
            </div>;
        }

        const successAlert = this.state.emailSent
            ? <div>
                <BS.Alert bsStyle="success">Email has been successfully sent to <strong>{this.props.user ? this.props.user.username : ""}</strong>.</BS.Alert>
                <p>
                    <a href="#" onClick={(e) => { this.toggleForgotPw(); e.preventDefault(); }}>Return to enter password &raquo;</a>
                    <br /><br />
                    Did not get an email?
                    Allow up to 30 seconds before <a href="#" onClick={(e) => { this.toggleSendingEmail(false, [], false); e.preventDefault(); }}>trying again</a>.
                </p>
            </div>
            : null;

        const sendEmailView = <div>
            {forgotPwView}
            {successAlert}
            {emailErrors}
        </div>;

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onValidSubmit={async (model) => await this.onSubmit(model)}
            schema={this.state.formModel.rules}
            showErrorsOnBlur={false}
            showErrorsOnSubmit={false}
            ref={(ref: any) => this.form = ref}
        >
            <h1>Welcome, {user ? user.firstName : "Guest"}!</h1>

            <BS.Collapse in={!isForgotPw} timeout={125}>
                <div>
                    <p className="mute">
                        <a href="#" onClick={(e) => this.onUserChange(e)}>Not {user ? Utility.joinSkipEmpty([user.firstName, user.lastName], " ") : "Guest"}?</a>
                    </p>

                    <div className="form-row">
                        <div className="form-field">
                            <p>Please enter your password</p>
                            <Comp.Editors.PasswordField formComponent={this} noFieldWrap css="txt" name="password" />
                        </div>
                    </div>

                    {pwFormButtons}
                </div>
            </BS.Collapse>

            <BS.Collapse in={isForgotPw} timeout={125} onExited={() => this.toggleSendingEmail(false, [], false)}>
                {sendEmailView}
            </BS.Collapse>

        </Comp.Validation.ValidatedForm>;
    }

    private focusPassword() {
        if (typeof document !== "undefined") {
            const parent = ReactDOM.findDOMNode(this);
            if (parent !== null && parent !== undefined) {
                const matches = parent.querySelectorAll(`[name="password"]`);
                if (matches.length) {
                    (matches[0] as HTMLInputElement).focus();
                }
            }
        }
    }

    private onUserChange(e: React.MouseEvent<HTMLAnchorElement>) {
        this.clearValidationErrors();
        this.props.onUserChange();
        e.preventDefault();
        return false;
    }

    private async onSendEmail() {
        this.toggleSendingEmail(true, [], false);

        const result = await this.props.api.post<boolean>(Api.endpoints.account.requestPasswordReset, {
            body: JSON.stringify({
                appId: Settings.AppID,
                returnUrl: this.props.returnUrl,
                username: this.props.user ? this.props.user.username : "",
            }),
        });

        let emailErrors: string[] = [];
        if (!result.isSuccess)
            emailErrors = result.errors.length ? result.errors : [result.message || ""];

        this.toggleSendingEmail(false, emailErrors, result.isSuccess);
    }

    private async onSubmit(model: Forms.Account.SignInPasswordData) {
        this.toggleProcessing(true);

        const result = await this.props.api.authorize({
            clientId: this.props.clientId,
            clientSecret: this.props.clientSecret,
            username: this.props.user ? this.props.user.username : "",
            password: model.password,
            grantType: "password",
        });

        this.toggleProcessing(false);

        if (result.isSuccess && result.data) {
            // then trigger success method
            AuthHelper.saveTokenToCookie(result.data.access_token, result.data.refresh_token);
            this.props.onSuccess(result.data.access_token, result.data.refresh_token);
        }
        else {
            this.handleValidationErrors([{
                inputName: "password",
                errors: [result.errorCode === ErrorCodes.Users_LoginFailed.id ? "Wrong password. Try again or click Forgot password to reset it." : result.errors.join("; ")],
            }]);
        }
    }

    private toggleForgotPw() {
        this.setState({ ...this.state, isForgotPw: !this.state.isForgotPw });
    }

    private toggleSendingEmail(toggled: boolean, emailErrors: string[], emailSent: boolean) {
        this.setState({
            ...this.state,
            emailErrors,
            emailSent,
            sendingEmail: toggled,
        });
    }
}

export const PasswordForm: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(PasswordFormComponent);



// WEBPACK FOOTER //
// ./src/containers/account/signin/components/PasswordForm.tsx